/**
 * Host-side gateway URL helpers for the embedded Dime Terminal.
 *
 * The terminal package (`@tradeparadigm/terminal-react`) owns pairing-token
 * and URL *storage* internally; this module owns the two things that are
 * Paradigm-specific and live outside the component:
 *
 *   1. Deriving a desk's default gateway URL from the trading-desk ticker +
 *      user id (multi-tenant `wss://<ticker>-<slug>.terminal.paradigm.tech`).
 *   2. Writing/clearing the component's stored URL + tokens from the
 *      settings screen, which renders outside `<Terminal>` and therefore
 *      can't reach the component's stores through React context.
 */

import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';

import { makeDefaultKeyFor } from '@tradeparadigm/terminal-react';

// Re-exported for host surfaces (e.g. the settings screen) so they don't
// need their own dependency on the terminal package.
export { isValidAgentUrl } from '@tradeparadigm/terminal-react';

declare const FE_DOMAIN: string | undefined;
declare const API_URL: string | undefined;
declare const DIME_TERMINAL_SLUG_SECRET: string | undefined;

/** Storage namespace the embedded terminal is configured with. */
export const PARADIGM_TERMINAL_NAMESPACE = 'paradigm';

// Same key shapes the component's stores read (config.namespace drives them),
// so settings-screen writes are visible to the terminal and vice versa.
const keyFor = makeDefaultKeyFor(PARADIGM_TERMINAL_NAMESPACE);

// --- Default URL Derivation ---

function slugSuffix(userId: number): string {
  const secret =
    typeof DIME_TERMINAL_SLUG_SECRET !== 'undefined' &&
    DIME_TERMINAL_SLUG_SECRET.length > 0
      ? DIME_TERMINAL_SLUG_SECRET
      : null;
  if (secret == null) return String(userId);
  return bytesToHex(hmac(sha256, secret, String(userId))).slice(0, 8);
}

const KNOWN_DOMAINS = [
  'paradigm.trade',
  'paradex.trade',
  'terminal.paradigm.tech',
];

function detectBaseDomain(): string | null {
  for (const candidate of [FE_DOMAIN, API_URL]) {
    try {
      if (typeof candidate === 'undefined' || candidate.length === 0) continue;
      const { hostname } = new URL(candidate);
      const match = KNOWN_DOMAINS.find(
        (d) => hostname.endsWith(`.${d}`) || hostname === d,
      );
      if (match != null) return match;
    } catch {
      // ignore unparseable URLs
    }
  }
  return null;
}

/**
 * Derive a default agent URL from ticker and user ID.
 * e.g. ticker="aqtl", userId=238
 *   → "wss://aqtl-6b13722d.terminal.paradigm.tech"
 */
export function deriveDefaultAgentUrl(ticker: string, userId: number): string {
  const baseDomain = detectBaseDomain();
  if (baseDomain == null) return '';
  return `wss://${ticker.toLowerCase()}-${slugSuffix(
    userId,
  )}.terminal.paradigm.tech`;
}

// --- Settings-screen bridge into the component's stores ---

/**
 * Read the gateway URL the terminal currently has stored for this user — the
 * same key the component reads on connect — so a settings screen can show and
 * edit the live value rather than a separate copy.
 */
export function getStoredAgentUrl(userId: string): string | null {
  try {
    return localStorage.getItem(keyFor('agentUrl', userId));
  } catch {
    // localStorage unavailable
    return null;
  }
}

/**
 * Write (or clear, with an empty string) the gateway URL the terminal
 * component will resolve on its next connect.
 */
export function setStoredAgentUrl(userId: string, url: string): void {
  try {
    const key = keyFor('agentUrl', userId);
    if (url.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, url);
    }
  } catch {
    // localStorage unavailable
  }
}

/**
 * Drop the pairing token and device token for a user. Tokens are keyed by
 * user id only (not by URL), so without an explicit reset the device token
 * from a prior OpenClaw instance persists and gets replayed against the new
 * instance on reconnect — which fails with "gateway token mismatch".
 *
 * Intentionally does NOT clear the URL: callers decide whether to rewrite
 * (settings save) or remove (clear flow) it.
 */
export function clearStoredAgentSession(userId: string): void {
  try {
    localStorage.removeItem(keyFor('agentToken', userId));
    localStorage.removeItem(keyFor('deviceToken', userId));
  } catch {
    // localStorage unavailable
  }
}
